import { useTracker } from '@archipro-website/tracker';

import { cn } from '@rocco/utils/cn';
import { TextTooltip } from '~/ui-rocco/components/TextTooltip';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import ProductAttributeEnum from '@modules/product/config/product-attribute';
import { useVariantOptionState } from '@modules/product/context/VariantOptionContext';
import { useVariantDecision } from '@modules/product/hook/use-variant-decision';
import type Variant from '@modules/product/type/variant';
import type ProductSize from '@modules/product/type/product-size';
import { makeGroupLabeller } from './variant-group-label';

export interface MobileVariantSelectorProps {
    variants: Variant[];
    /** Sizes actually used by a variant (the legacy `sizesInUse` filter). */
    sizes: ProductSize[];
    /**
     * Allow the Size group to collapse into a dropdown when the list is long
     * (more than {@link SIZE_DROPDOWN_THRESHOLD} options). Used in the desktop
     * purchase drawer where a long size list (e.g. 13 dimensional options) would
     * otherwise dominate; shorter lists stay as chips, and the full chip list
     * always lives in the page meta row.
     */
    sizeAsDropdown?: boolean;
    /**
     * Render only these attribute groups. Omitted, every group shows.
     *
     * The product page's spec list puts one group in each of its rows, so it
     * mounts this once per row asking for a single attribute. The selector is
     * the same one the drawer uses either way — the behaviour a reader gets in
     * the list is the behaviour they get in the drawer because it is the same
     * component, not a second implementation of it.
     */
    attributeIDs?: number[];
    /** Include the Size group. */
    showSizes?: boolean;
    /**
     * Print each group's own label. Off where the surface already names the
     * group, as the spec list's rows do.
     */
    showGroupLabels?: boolean;
}

/** Sentinel value for the "Not Applicable" (no size) option in the dropdown. */
const NO_SIZE_VALUE = '0';

/** Above this many size options, the drawer collapses the group to a dropdown. */
const SIZE_DROPDOWN_THRESHOLD = 6;

const GROUP_LABEL =
    'm-0 font-sans text-[0.9375rem] font-medium tracking-[-0.01em] text-[#7f7f7f]';
// text-[0.75rem] (12px, per the Figma chips) — this config remaps `text-xs`
// to 0.625rem/10px, below legibility.
const CHIP_BASE =
    'inline-flex items-center rounded-[var(--radius-btn-xs)] border border-black px-3.5 font-sans text-[0.75rem] uppercase leading-[1.875rem] transition-colors';

/**
 * The inline mobile variant selector (BC-4524, Figma 1855:13325) — the legacy
 * selection logic (`useVariantDecision` against the shared option/selection
 * contexts) re-skinned in the revamp language: 40px colour swatches and
 * uppercase pill chips for materials / other attributes / sizes. Non-generic
 * groups keep the legacy "Not Applicable" empty option. Logs `DropDownSelect`
 * on every pick, matching the legacy pickers.
 */
export const MobileVariantSelector = ({
    variants,
    sizes: allSizes,
    sizeAsDropdown = false,
    attributeIDs,
    showSizes = true,
    showGroupLabels = true,
}: MobileVariantSelectorProps) => {
    const { attributeMap, decision, canonicalOptionIds } =
        useVariantOptionState();
    // Compare option IDs through their canonical twin — variants may reference
    // duplicate option records that display dedupes away (see B2).
    const canonical = (id: number) => canonicalOptionIds.get(id) ?? id;
    const { pickOption, pickSize } = useVariantDecision(variants);
    const tracker = useTracker();

    const logPick = (id: number) => {
        tracker.log('DropDownSelect', {
            url: new URL(window.location.href),
            targetTracker: 'archiproTracker',
            data: { ExtraData: id },
        });
    };

    const allAttributes = [...attributeMap.values()];
    // Availability is still judged against every variant; only what is drawn
    // is narrowed, so a group shown on its own still dims the options that no
    // variant pairs with the current selection elsewhere.
    const attributes = attributeIDs
        ? allAttributes.filter((attribute) =>
              attributeIDs.includes(attribute.ID),
          )
        : allAttributes;
    const sizes = showSizes ? allSizes : [];
    const sizeIsGeneric =
        variants.length === 0 || variants.every((v) => Boolean(v.Size));
    // Only collapse to a dropdown once the list is long enough to be unwieldy.
    const useSizeDropdown =
        sizeAsDropdown && sizes.length > SIZE_DROPDOWN_THRESHOLD;

    // Availability: an option/size is "available" when at least one variant pairs
    // it with the current selections in the OTHER dimensions. Unavailable ones are
    // dimmed so the shopper can see which combinations don't exist — they stay
    // clickable, because a pick auto-resolves to the nearest valid variant (so the
    // shopper is never stuck in a dead-end combination).
    const matchesOtherOptions = (variant: Variant, exceptAttributeID: number) =>
        Object.entries(decision.options).every(([attrID, optID]) => {
            if (Number(attrID) === exceptAttributeID || !optID) return true;
            return variant.Options.some((o) => canonical(o.ID) === optID);
        });
    const optionAvailable = (attributeID: number, optionID: number) =>
        optionID === 0 ||
        variants.some(
            (v) =>
                v.Options.some(
                    (o) => canonical(o.ID) === canonical(optionID),
                ) &&
                matchesOtherOptions(v, attributeID) &&
                (!decision.size || v.Size?.ID === decision.size),
        );
    const sizeAvailable = (sizeID: number) =>
        sizeID === 0 ||
        variants.some(
            (v) => v.Size?.ID === sizeID && matchesOtherOptions(v, -1),
        );

    // Named from every group, not just the drawn ones: the labeller
    // disambiguates names against each other, and narrowing the input would
    // give the same attribute a different label in the list than in the
    // drawer.
    const labelFor = makeGroupLabeller(
        allAttributes.map((attribute) => attribute.Name),
    );

    if (attributes.length === 0 && sizes.length === 0) return null;

    return (
        <div className="flex flex-col gap-[1.875rem]">
            {attributes.map((attribute) => {
                const selectedId = decision.options[attribute.ID] ?? 0;
                const isColour = attribute.ID === ProductAttributeEnum.Colour;
                const options = attribute.isGeneric
                    ? attribute.options
                    : [
                          {
                              ID: 0,
                              Label: 'Not Applicable',
                              Code: null,
                              Image: null,
                          },
                          ...attribute.options,
                      ];
                const pick = (id: number) => {
                    logPick(id);
                    pickOption({ ID: id, attributeID: attribute.ID });
                };

                return (
                    <div key={attribute.ID} className="flex flex-col gap-3">
                        {showGroupLabels ? (
                            <p className={GROUP_LABEL}>
                                {labelFor(attribute.Name)}
                            </p>
                        ) : null}
                        <div className="flex flex-wrap items-center gap-1.5">
                            {options.map((option) => {
                                const isSelected = selectedId === option.ID;
                                const available = optionAvailable(
                                    attribute.ID,
                                    option.ID,
                                );
                                // A colourway with neither a texture nor a
                                // hex has nothing a circle can show — it
                                // drew as a blank chip among the swatches.
                                // It takes the chip treatment instead, still
                                // pressable and still slashed when
                                // unavailable.
                                const unknownColour =
                                    isColour &&
                                    option.ID !== 0 &&
                                    !option.Image?.Desktop &&
                                    !option.Code;
                                if (unknownColour) {
                                    return (
                                        <button
                                            key={option.ID}
                                            type="button"
                                            aria-label={`Colour: ${option.Label}`}
                                            aria-pressed={isSelected}
                                            onClick={() => pick(option.ID)}
                                            className={cn(
                                                'inline-flex h-10 items-center rounded-full border border-black/15 bg-transparent px-4 font-sans text-sm text-[#323232]',
                                                isSelected &&
                                                    'ring-1 ring-black ring-offset-2',
                                                !available &&
                                                    !isSelected &&
                                                    'text-[#9b9b9b] line-through',
                                            )}
                                        >
                                            {option.Label}
                                        </button>
                                    );
                                }
                                return isColour ? (
                                    // Styled hover tooltip with the colour name
                                    // (matches the meta-row swatches); renders
                                    // above the drawer via z-tooltip.
                                    <TextTooltip
                                        key={option.ID}
                                        content={option.Label}
                                    >
                                        <button
                                            type="button"
                                            aria-label={`Colour: ${option.Label}`}
                                            aria-pressed={isSelected}
                                            onClick={() => pick(option.ID)}
                                            className="flex size-10 shrink-0 items-center justify-center rounded-full"
                                        >
                                            <span
                                                className={cn(
                                                    'relative block size-[2.0625rem] overflow-hidden rounded-full border border-black/10 bg-cover bg-center',
                                                    // Ring hugs the swatch (2px gap)
                                                    // rather than ringing the 40px tap
                                                    // target, so it sits flush.
                                                    isSelected &&
                                                        'ring-1 ring-black ring-offset-2',
                                                    option.ID === 0 &&
                                                        'bg-[linear-gradient(to_top_left,transparent_46%,#c4c4c4_48%,#c4c4c4_52%,transparent_54%)]',
                                                )}
                                                style={
                                                    option.Image?.Desktop
                                                        ? {
                                                              backgroundImage: `url(${option.Image.Desktop})`,
                                                          }
                                                        : option.ID !== 0
                                                          ? {
                                                                backgroundColor:
                                                                    option.Code ??
                                                                    '#f1f1f1',
                                                            }
                                                          : undefined
                                                }
                                            >
                                                {!available && !isSelected ? (
                                                    // Diagonal slash marks an
                                                    // unavailable colour without
                                                    // hiding it; difference-blend
                                                    // keeps the line visible on any
                                                    // swatch (light, dark or grey).
                                                    <span
                                                        aria-hidden
                                                        className="absolute top-1/2 left-1/2 h-[1.5px] w-[150%] -translate-x-1/2 -translate-y-1/2 rotate-45 bg-white mix-blend-difference"
                                                    />
                                                ) : null}
                                            </span>
                                        </button>
                                    </TextTooltip>
                                ) : (
                                    <button
                                        key={option.ID}
                                        type="button"
                                        aria-pressed={isSelected}
                                        onClick={() => pick(option.ID)}
                                        className={cn(
                                            CHIP_BASE,
                                            'transition-colors',
                                            isSelected
                                                ? 'bg-[#181818] text-white'
                                                : available
                                                  ? 'text-[#1f1f21]'
                                                  : 'border-black/30 text-[#a3a3a3] line-through',
                                        )}
                                    >
                                        {option.Label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                );
            })}

            {sizes.length > 0 ? (
                <div className="flex flex-col gap-3">
                    {showGroupLabels ? (
                        <p className={GROUP_LABEL}>Size</p>
                    ) : null}
                    {useSizeDropdown ? (
                        <Select
                            value={
                                decision.size
                                    ? String(decision.size)
                                    : sizeIsGeneric
                                      ? // Empty string keeps the Select
                                        // controlled (shows the placeholder)
                                        // rather than flipping to uncontrolled.
                                        ''
                                      : NO_SIZE_VALUE
                            }
                            onValueChange={(value) => {
                                const sizeID = Number(value);
                                logPick(sizeID);
                                pickSize(sizeID);
                            }}
                        >
                            <SelectTrigger className="h-11 w-full rounded-[var(--radius-btn-m)] px-4 font-sans text-[0.75rem] uppercase">
                                <SelectValue placeholder="Select a size" />
                            </SelectTrigger>
                            <SelectContent>
                                {!sizeIsGeneric ? (
                                    <SelectItem value={NO_SIZE_VALUE}>
                                        Not Applicable
                                    </SelectItem>
                                ) : null}
                                {sizes.map((size) => (
                                    <SelectItem
                                        key={size.ID}
                                        value={String(size.ID)}
                                        className={cn(
                                            !sizeAvailable(size.ID) &&
                                                decision.size !== size.ID &&
                                                'text-[#a3a3a3] line-through',
                                        )}
                                    >
                                        {size.FullTitle}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    ) : (
                        <div className="flex flex-wrap items-center gap-1.5">
                            {!sizeIsGeneric ? (
                                <button
                                    type="button"
                                    aria-pressed={!decision.size}
                                    onClick={() => {
                                        logPick(0);
                                        pickSize(0);
                                    }}
                                    className={cn(
                                        CHIP_BASE,
                                        !decision.size
                                            ? 'bg-[#181818] text-white'
                                            : 'text-[#1f1f21]',
                                    )}
                                >
                                    Not Applicable
                                </button>
                            ) : null}
                            {sizes.map((size) => (
                                <button
                                    key={size.ID}
                                    type="button"
                                    aria-pressed={decision.size === size.ID}
                                    onClick={() => {
                                        logPick(size.ID);
                                        pickSize(size.ID);
                                    }}
                                    className={cn(
                                        CHIP_BASE,
                                        'transition-colors',
                                        decision.size === size.ID
                                            ? 'bg-[#181818] text-white'
                                            : sizeAvailable(size.ID)
                                              ? 'text-[#1f1f21]'
                                              : 'border-black/30 text-[#a3a3a3] line-through',
                                    )}
                                >
                                    {size.FullTitle}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            ) : null}
        </div>
    );
};
